import React, { useState } from "react";
import { DefaultButton, FontIcon } from "@fluentui/react";
import CompanySectionCard from "@/screen/Companies/components/CompanySectionCard";
import { CompanyOnboardingStrings } from "@/screen/Companies/companyOnboarding.strings";
import type { SelectableRepresentativeDto } from "@/services/CompanyOnboardingService";
import { InfoBanner } from "@/screen/Companies/Styles/CompanyOnboardingStyles";
import { Field, Input, Select } from "@/screen/Companies/Styles/CompanyFormFieldStyles";
import { LinkButton, RepresentativeAddRow, RepresentativeCard } from "@/screen/Companies/Styles/CompanyDetailsStyles";
import { BeneficialOwnerRole, RoleOptions, SignOptions } from "@/screen/Companies/CompanyDetails/companyDetailsForm";
import type { CompanyRepresentativeFormRow } from "@/screen/Companies/CompanyDetails/companyDetailsForm";

interface CompanyRepresentativesSectionProps
{
    id?: string;
    number: number;
    rows: CompanyRepresentativeFormRow[];
    available: SelectableRepresentativeDto[];
    hasSelectable: boolean;
    borrowedIds: number[];
    disabled: boolean;
    onAdd: (userIdentityId: number) => void;
    onRemove: (userIdentityId: number) => void;
    onChange: (userIdentityId: number, field: "role" | "sign" | "ownership", value: string) => void;
}

// Sección 6: representantes elegidos entre los intervinientes propios y prestados, con su rol, firma y titularidad
const CompanyRepresentativesSection: React.FC<CompanyRepresentativesSectionProps> = ({ id, number, rows, available, hasSelectable, borrowedIds, disabled, onAdd, onRemove, onChange }) =>
{
    const [candidateId, setCandidateId] = useState<string>("");

    const handleAdd = (): void =>
    {
        const id = Number(candidateId);
        if(id > 0)
        {
            onAdd(id);
            setCandidateId("");
        }
    };

    return(
        <CompanySectionCard id={id} number={number} title={CompanyOnboardingStrings.representativesTitle} description={CompanyOnboardingStrings.representativesDescription}>
            <InfoBanner role="note">
                <FontIcon iconName="Info" />
                <span>{hasSelectable ? CompanyOnboardingStrings.representativesInfo : CompanyOnboardingStrings.noSelectableRepresentatives}</span>
            </InfoBanner>
            {!disabled && available.length > 0 &&
            (
                <RepresentativeAddRow>
                    <Field>
                        <span>Buscar interviniente</span>
                        <Select value={candidateId} onChange={(e) => setCandidateId(e.target.value)}>
                            <option value="">{CompanyOnboardingStrings.representativeSearchPlaceholder}</option>
                            {available.map((item) =>
                            (
                                <option key={item.userIdentityId} value={item.userIdentityId}>
                                    {item.fullName}{item.maskedIdDocument ? ` · ${item.maskedIdDocument}` : ""}{item.isOwn ? "" : ` (${CompanyOnboardingStrings.borrowedRepresentative})`}
                                </option>
                            ))}
                        </Select>
                    </Field>
                    <DefaultButton className="button tiny-secondary-button" onClick={handleAdd} disabled={candidateId === ""}>
                        {CompanyOnboardingStrings.addRepresentative}
                    </DefaultButton>
                </RepresentativeAddRow>
            )}
            {rows.map((row) =>
            {
                const isOwner = row.role === BeneficialOwnerRole;
                return(
                    <RepresentativeCard key={row.userIdentityId}>
                        <div className="who">
                            <b>{row.fullName}</b>
                            <small>
                                {row.maskedIdDocument}
                                {borrowedIds.includes(row.userIdentityId) ? ` · ${CompanyOnboardingStrings.borrowedRepresentative}` : ""}
                            </small>
                        </div>
                        <Field>
                            <span>Rol <b>*</b></span>
                            <Select value={row.role} onChange={(e) => onChange(row.userIdentityId, "role", e.target.value)}>
                                <option value="">-- Seleccionar --</option>
                                {RoleOptions.map((option) => <option key={option.value} value={option.value}>{option.text}</option>)}
                            </Select>
                        </Field>
                        <Field>
                            <span>Firma <b>*</b></span>
                            <Select value={row.sign} onChange={(e) => onChange(row.userIdentityId, "sign", e.target.value)}>
                                <option value="">-- Seleccionar --</option>
                                {SignOptions.map((option) => <option key={option.value} value={option.value}>{option.text}</option>)}
                            </Select>
                        </Field>
                        <Field>
                            <span>Titularidad %{isOwner && <b> *</b>}</span>
                            <Input type="number" min={0} max={100} step="0.01" value={isOwner ? row.ownership : ""} disabled={!isOwner}
                                onChange={(e) => onChange(row.userIdentityId, "ownership", e.target.value)}
                            />
                        </Field>
                        {!disabled &&
                        (
                            <LinkButton type="button" title={CompanyOnboardingStrings.removeRepresentative} aria-label={CompanyOnboardingStrings.removeRepresentative} onClick={() => onRemove(row.userIdentityId)}>
                                <FontIcon iconName="Delete" />
                            </LinkButton>
                        )}
                    </RepresentativeCard>
                );
            })}
        </CompanySectionCard>
    );
};

export default CompanyRepresentativesSection;
